import { Stack, Box } from '@mui/material';
import { Button, colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { PromptGroup } from '@server/prompts';
import { SearchInput } from '../../../common/SearchInput';
import { useMemo, useState } from 'react';
import {
  AdvancedFilter,
  AdvancedFilterItem,
} from '../../../common/filters/advanced-filter/AdvancedFilter';
import { DatePickerWrapper } from '../../../common/datepicker/DatePickerWrapper';
import { getPromptsEmptyState, promptTableColumns } from './rightPanelHelpers';
import { usePromptsTable } from './usePromptsTable';
import { PromptsTable } from './PromptsTable';
import { PromptsViewLayout } from './PromptsViewLayout';
import { PromptWithCategory } from '../types';
import { PromptLibraryIcon } from '../PromptLibraryIcon';
import { useSelector } from 'react-redux';
import { selectAccountUsers } from '../../../../reducers/accountUsersReducer';
import { isSweepDefaultPrompt, SWEEP_AUTHOR_ID } from '@server/ai-prompts.config';
import { UNCATEGORIZED_FILTER_ID } from '../usePromptGroupState';

interface AllPromptsViewProps {
  prompts: PromptWithCategory[];
  categories: PromptGroup[];
  onClose: () => void;
  onCreatePrompt: () => void;
  onPromptClick: (prompt: PromptWithCategory) => void;
  onDeletePrompt: (promptId: string) => void;
}

export const AllPromptsView = ({
  prompts,
  categories,
  onClose,
  onCreatePrompt,
  onPromptClick,
  onDeletePrompt,
}: AllPromptsViewProps) => {
  const [selectedCategoryFilters, setSelectedCategoryFilters] = useState<string[]>([]);
  const [selectedUserFilters, setSelectedUserFilters] = useState<string[]>([]);
  const accountUsers = useSelector(selectAccountUsers);

  const {
    searchTerm,
    setSearchTerm,
    setDateFilter,
    dateFilter,
    expandedRowId,
    setExpandedRowId,
    rows,
  } = usePromptsTable({
    prompts,
    selectedCategoryFilters,
    selectedUserFilters,
    onEditPrompt: onPromptClick,
    onUseInChatSuccess: onClose,
    onDeletePrompt,
  });
  const hasAnyPrompts = prompts.length > 0;

  const columns = promptTableColumns;

  const categoryFilterItems: AdvancedFilterItem[] = useMemo(() => {
    const hasUncategorizedPrompts = prompts.some((p) => !p.promptGroupId);

    const categoryItems = categories.map((cat) => ({
      labelDecoration: <PromptLibraryIcon icon={cat.iconName} size="medium" />,
      value: cat.id,
      label: cat.name,
    }));

    if (hasUncategorizedPrompts) {
      categoryItems.unshift({
        labelDecoration: <PromptLibraryIcon icon={'Map'} size="medium" />,
        label: '(Uncategorized)',
        value: UNCATEGORIZED_FILTER_ID,
      });
    }

    return categoryItems;
  }, [categories, prompts]);

  const userFilterItems: AdvancedFilterItem[] = useMemo(() => {
    const userIds = new Set(prompts.map((p) => p.updatedById).filter(Boolean));
    const hasSweepPrompts = prompts.some((p) =>
      isSweepDefaultPrompt(p.name, p.categoryName, p.updatedById),
    );
    const hasBlankPrompts = prompts.some(
      (p) => !p.updatedById && !isSweepDefaultPrompt(p.name, p.categoryName, p.updatedById),
    );

    const userItems: AdvancedFilterItem[] = (accountUsers || [])
      .filter((user) => user.id && userIds.has(user.id))
      .map((user) => ({
        label: user.name || user.email || 'Unknown',
        value: user.id as string,
      }));

    if (hasSweepPrompts) {
      userItems.push({ label: 'Sweep', value: SWEEP_AUTHOR_ID });
    }

    userItems.sort((a, b) => a.label.localeCompare(b.label));

    if (hasBlankPrompts) {
      userItems.unshift({ label: '(Blank)', value: '__blank__' });
    }

    return userItems;
  }, [accountUsers, prompts]);

  const { title: emptyStateTitle, description: emptyStateDescription } = useMemo(
    () =>
      getPromptsEmptyState({
        hasPrompts: prompts.length > 0,
        hasActiveFilters:
          searchTerm.trim().length > 0 ||
          selectedCategoryFilters.length > 0 ||
          selectedUserFilters.length > 0 ||
          Boolean(dateFilter),
      }),
    [
      dateFilter,
      prompts.length,
      searchTerm,
      selectedCategoryFilters.length,
      selectedUserFilters.length,
    ],
  );

  return (
    <PromptsViewLayout
      header={
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            padding: 3,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, pr: 2 }}>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClearButtonClick={() => setSearchTerm('')}
              placeholder="Search for Prompts or Categories"
              withFixedMagnifyingGlassIcon
              TextFieldProps={{ value: searchTerm }}
              size="default"
            />
          </Box>
          <Button startIconName="Plus" size="medium" onClick={onCreatePrompt}>
            New Prompt
          </Button>
          <IconButton variant="flat" size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      }
      filters={
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            padding: 3,
            paddingTop: 0,
            flexShrink: 0,
          }}
        >
          <Typography variant="body" color={colors.grey[800]}>
            Filters:
          </Typography>
          <AdvancedFilter
            disabled={!hasAnyPrompts}
            texts={{
              title: 'Categories',
            }}
            items={categoryFilterItems}
            selectedItems={selectedCategoryFilters}
            onSelectedItemsChange={(items) => setSelectedCategoryFilters(items)}
          />
          <DatePickerWrapper
            disabled={!hasAnyPrompts}
            onSelectDate={setDateFilter}
            placeholder="Last modified"
            showClearButton
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          />
          <AdvancedFilter
            disabled={!hasAnyPrompts}
            texts={{
              title: 'Last modified by',
            }}
            items={userFilterItems}
            selectedItems={selectedUserFilters}
            onSelectedItemsChange={(items) => setSelectedUserFilters(items)}
          />
        </Stack>
      }
    >
      <PromptsTable
        rows={rows}
        columns={columns}
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        emptyStateTitle={emptyStateTitle}
        emptyStateDescription={emptyStateDescription}
      />
    </PromptsViewLayout>
  );
};
